<template>
  <el-dialog
    v-model="visible"
    title="路线详情"
    width="90%"
    top="5vh"
    :before-close="handleClose"
    class="map-dialog"
    destroy-on-close
  >
    <div class="map-modal-container">
      <!-- 左侧地图 -->
      <div class="map-section">
        <div id="map-container" class="map-container"></div>
        <!-- 地图控制按钮 -->
        <div class="map-controls">
          <el-button-group>
            <el-button size="small" @click="fitBounds">
              <el-icon><FullScreen /></el-icon> 全览
            </el-button>
            <el-button size="small" @click="downloadMap">
              <el-icon><Download /></el-icon> 下载
            </el-button>
          </el-button-group>
        </div>
      </div>

      <!-- 右侧详情 -->
      <div class="detail-section">
        <el-tabs v-model="activeTab" type="border-card">
          <!-- 行程表 -->
          <el-tab-pane label="📅 行程" name="itinerary">
            <ItineraryTable :itinerary="route.dailyItinerary" />
          </el-tab-pane>

          <!-- 预算表 -->
          <el-tab-pane label="💰 预算" name="budget">
            <BudgetTable :budget="route.budgetDetails" />
          </el-tab-pane>

          <!-- 景点详情 -->
          <el-tab-pane label="🏞️ 景点" name="pois">
            <div class="pois-list">
              <el-card 
                v-for="(poi, index) in route.pois" 
                :key="index"
                class="poi-detail-card"
                shadow="hover"
              >
                <div class="poi-header">
                  <span class="poi-order">{{ index + 1 }}</span>
                  <h4>{{ poi.name }}</h4>
                  <el-tag v-if="poi.rating" type="warning" size="small">
                    ⭐ {{ poi.rating }}
                  </el-tag>
                </div>
                <p class="poi-desc">{{ poi.description }}</p>
                <div v-if="poi.address" class="poi-address">
                  <el-icon><Location /></el-icon>
                  {{ poi.address }}
                </div>
              </el-card>
            </div>
          </el-tab-pane>

          <!-- 实用贴士 -->
          <el-tab-pane label="💡 贴士" name="tips">
            <el-timeline>
              <el-timeline-item
                v-for="(tip, index) in route.tips"
                :key="index"
                :type="index % 2 === 0 ? 'primary' : 'success'"
                :icon="InfoFilled"
              >
                {{ tip }}
              </el-timeline-item>
            </el-timeline>
          </el-tab-pane>
        </el-tabs>

        <!-- 底部操作 -->
        <div class="modal-actions">
          <el-button 
            type="primary" 
            size="large" 
            @click="shareRoute"
            class="share-route-btn"
          >
            <el-icon><Share /></el-icon>
            分享此路线
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import AMapLoader from '@amap/amap-jsapi-loader'
import { FullScreen, Download, Location, Share, InfoFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import ItineraryTable from './ItineraryTable.vue'
import BudgetTable from './BudgetTable.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  route: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['update:modelValue'])

const visible = ref(props.modelValue)
const activeTab = ref('itinerary')
let map = null
let markers = []
let polyline = null

watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val) {
    nextTick(() => {
      initMap()
    })
  }
})

watch(() => visible.value, (val) => {
  emit('update:modelValue', val)
})

const initMap = async () => {
  try {
    const AMap = await AMapLoader.load({
      key: '0b745a9a7a9441bce1468bfbae6ccf4c',
      version: '2.0',
      plugins: ['AMap.Driving', 'AMap.Scale', 'AMap.ToolBar']
    })

    map = new AMap.Map('map-container', {
      zoom: 10,
      center: [116.397428, 39.90923]
    })

    // Add scale and toolbar
    map.addControl(new AMap.Scale())
    map.addControl(new AMap.ToolBar())

    // Add markers for each POI
    if (props.route.pois && props.route.pois.length > 0) {
      addMarkers(AMap)
    }

  } catch (error) {
    console.error('Map initialization error:', error)
    ElMessage.error('地图加载失败，请刷新重试')
  }
}

const addMarkers = (AMap) => {
  // Clear existing markers
  if (markers.length > 0) {
    map.remove(markers)
    markers = []
  }

  const path = []

  props.route.pois.forEach((poi, index) => {
    if (poi.location) {
      const [lng, lat] = poi.location.split(',').map(Number)
      
      // Create marker
      const marker = new AMap.Marker({
        position: [lng, lat],
        title: poi.name,
        label: {
          content: `${index + 1}. ${poi.name}`,
          offset: new AMap.Pixel(0, -30)
        }
      })

      // Add info window
      const infoWindow = new AMap.InfoWindow({
        content: `
          <div style="padding: 10px; max-width: 200px;">
            <h4 style="margin: 0 0 5px 0;">${poi.name}</h4>
            <p style="margin: 0; color: #666; font-size: 12px;">${poi.description || ''}</p>
            ${poi.rating ? `<p style="margin: 5px 0 0 0; color: #ff9900;">⭐ ${poi.rating}</p>` : ''}
          </div>
        `,
        offset: new AMap.Pixel(0, -30)
      })

      marker.on('click', () => {
        infoWindow.open(map, marker.getPosition())
      })

      markers.push(marker)
      path.push([lng, lat])
    }
  })

  // Add all markers
  map.add(markers)

  // Draw route line if multiple points
  if (path.length > 1) {
    if (polyline) {
      map.remove(polyline)
    }
    polyline = new AMap.Polyline({
      path: path,
      strokeColor: '#00B5FF',
      strokeWeight: 6,
      strokeOpacity: 0.8,
      showDir: true
    })
    map.add(polyline)
    map.setFitView()
  }
}

const fitBounds = () => {
  if (map && markers.length > 0) {
    map.setFitView()
  }
}

const downloadMap = () => {
  ElMessage.info('地图下载功能开发中...')
}

const shareRoute = () => {
  if (props.route.shareUrl) {
    navigator.clipboard.writeText(props.route.shareUrl)
    ElMessage.success('分享链接已复制到剪贴板！')
  } else {
    ElMessage.warning('分享链接暂不可用')
  }
}

const handleClose = (done) => {
  if (map) {
    map.destroy()
    map = null
  }
  markers = []
  polyline = null
  done()
}
</script>

<style scoped>
.map-modal-container {
  display: flex;
  gap: 20px;
  height: 70vh;
}

.map-section {
  flex: 1;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}

.map-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.map-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
}

.detail-section {
  width: 400px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.pois-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 400px;
  overflow-y: auto;
}

.poi-detail-card {
  border-radius: 8px;
}

.poi-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.poi-order {
  width: 24px;
  height: 24px;
  background: #00B5FF;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

.poi-header h4 {
  margin: 0;
  flex: 1;
}

.poi-desc {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.poi-address {
  margin-top: 8px;
  color: #999;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.modal-actions {
  margin-top: auto;
  padding-top: 15px;
}

.share-route-btn {
  width: 100%;
}

@media (max-width: 768px) {
  .map-modal-container {
    flex-direction: column;
    height: auto;
  }
  
  .map-section {
    height: 300px;
  }
  
  .detail-section {
    width: 100%;
  }
}
</style>
